import {
  InspectionReportViewModel,
  NOT_RECORDED,
  QuoteCaseViewModel,
  QuoteDetailsViewModel,
  ReportHeaderViewModel,
  SectionViewModel,
  TaskRowViewModel,
  TaskStatus,
  TireCardViewModel,
  TreadLegendViewModel,
} from './inspection-report.view-model';
import {
  ADDITIONAL_CHARGES_LABEL,
  CASE_TOTAL_LABEL,
  CAUSE_LABEL,
  CONCERN_LABEL,
  CONTINUED_SUFFIX,
  CORRECTION_LABEL,
  EMPLOYEE_LABEL,
  EXPIRES_LABEL,
  LABOUR_HOURS_LABEL,
  LABOUR_SUBTOTAL_LABEL,
  LEGEND_ENTRIES,
  NOT_INSPECTED_LABEL,
  PARTS_SUBTOTAL_LABEL,
  PSI_BEFORE_LABEL,
  PSI_SET_TO_LABEL,
  QUOTE_CASE_LABEL,
  QUOTE_CASE_NAME_SEPARATOR,
  QUOTE_DETAILS_TITLE,
  QUOTE_TOTAL_LABEL,
  ReportTheme,
  SHOP_SUPPLIES_NOTE,
  SUBTOTALS_LABEL,
  TAX_LABEL,
  TREAD_DEPTH_LABEL,
  WEAR_PATTERN_LABEL,
} from './report-theme';

export const FontStyle = {
  Regular: 'regular',
  Bold: 'bold',
} as const;
export type FontStyle = typeof FontStyle[keyof typeof FontStyle];

export const TextColour = {
  Text: 'text',
  Muted: 'muted',
} as const;
export type TextColour = typeof TextColour[keyof typeof TextColour];

export const StrokeColour = {
  Rule: 'rule',
  Border: 'border',
} as const;
export type StrokeColour = typeof StrokeColour[keyof typeof StrokeColour];

export const FillColour = {
  Band: 'band',
} as const;
export type FillColour = typeof FillColour[keyof typeof FillColour];

export type TextMeasurer = (text: string, size: number, style: FontStyle) => number;

// Every y is measured down from the top edge of the page; the painter flips to PDF space.
export interface TextOp {
  readonly kind: 'text';
  readonly x: number;
  readonly baseline: number;
  readonly text: string;
  readonly size: number;
  readonly style: FontStyle;
  readonly colour: TextColour;
  readonly underline?: true;
}

export interface IconOp {
  readonly kind: 'icon';
  readonly status: TaskStatus;
  readonly x: number;
  readonly top: number;
  readonly size: number;
}

export interface LineOp {
  readonly kind: 'line';
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly thickness: number;
}

export interface RectOp {
  readonly kind: 'rect';
  readonly x: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  // 0 draws no outline.
  readonly thickness: number;
  readonly stroke: StrokeColour;
  readonly fill?: FillColour;
}

export type LayoutOp = TextOp | IconOp | LineOp | RectOp;

export interface LayoutPage {
  readonly ops: readonly LayoutOp[];
}

export interface ReportLayout {
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly pages: readonly LayoutPage[];
}

interface TrailingText {
  readonly text: string;
  readonly width: number;
  readonly colour: TextColour;
}

interface Column {
  readonly left: number;
  readonly right: number;
  y: number;
}

// A unit of section content that is never split: a task row, a pair of tire cards, the tread
// legend. `keepWithNext` holds the block on the same column as the one after it.
interface Block {
  readonly height: number;
  readonly keepWithNext: boolean;
  readonly place: (column: Column) => void;
}

interface MeasuredCard {
  readonly card: TireCardViewModel;
  readonly titleLines: readonly string[];
  readonly bodyLines: readonly string[];
  readonly height: number;
}

interface LegendItem {
  readonly status: TaskStatus;
  readonly text: string;
  readonly width: number;
}

// A labelled, wrapped text field on the quote page (Concern, Cause, Correction).
interface QuoteField {
  readonly label: string;
  readonly lines: readonly string[];
}

// A label-left, value-right line of the quote page's subtotals block.
interface QuoteAmount {
  readonly label: string;
  readonly value: string;
  readonly style: FontStyle;
}

// One printed line of a case's fields: a bold label at the margin or an indented line of text.
interface QuoteFieldLine {
  readonly text: string;
  readonly isLabel: boolean;
}

const CONTENT_LEFT = ReportTheme.margin;
const CONTENT_RIGHT = ReportTheme.pageWidth - ReportTheme.margin;
const CONTENT_WIDTH = CONTENT_RIGHT - CONTENT_LEFT;
const CONTENT_TOP = ReportTheme.margin;
const CONTENT_BOTTOM = ReportTheme.pageHeight - ReportTheme.margin - ReportTheme.footerHeight;
const HEADING_RULE_HEIGHT = ReportTheme.headingRuleGap + ReportTheme.ruleThickness;
const ROW_TEXT_INDENT = ReportTheme.rowIndent + ReportTheme.iconSize + ReportTheme.iconGap;
const TRAILING_TEXT_GAP = 8;
const HEADER_COLUMN_GAP = 12;
const HEADER_COLUMN_WIDTH = (CONTENT_WIDTH - HEADER_COLUMN_GAP) / 2;
export const COLUMN_WIDTH = (CONTENT_WIDTH - ReportTheme.columnGap) / 2;
export const CARD_WIDTH = (COLUMN_WIDTH - ReportTheme.cardGap) / 2;
const CARD_INNER_WIDTH = CARD_WIDTH - ReportTheme.cardPadding * 2;
const CARD_TITLE_TEXT_LEFT = ReportTheme.cardPadding + ReportTheme.iconSize + ReportTheme.iconGap;
const CARDS_PER_BLOCK = 4;
export const QUOTE_TEXT_COLUMN_WIDTH = CONTENT_WIDTH * ReportTheme.quoteTextColumnShare;
export const QUOTE_TOTALS_COLUMN_WIDTH = CONTENT_WIDTH * ReportTheme.quoteTotalsColumnShare;
const QUOTE_TOTALS_COLUMN_LEFT = CONTENT_RIGHT - QUOTE_TOTALS_COLUMN_WIDTH;
const QUOTE_FIELD_WIDTH = QUOTE_TEXT_COLUMN_WIDTH - ReportTheme.quoteFieldIndent;
// The divider that closes a case and the gap before the next one.
const QUOTE_CASE_TRAILER_HEIGHT = ReportTheme.quoteDividerGap + ReportTheme.ruleThickness + ReportTheme.quoteCaseGap;

export function layoutInspectionReport(viewModel: InspectionReportViewModel, measure: TextMeasurer): ReportLayout {
  const composer = new PageComposer(measure, viewModel.treadLegend);

  composer.placeHeader(viewModel.header);
  composer.placeLegend();
  composer.beginColumns();
  viewModel.sections.forEach((section) => composer.placeSection(section));
  if (viewModel.quote !== null) {
    composer.placeQuoteDetails(viewModel.quote);
  }
  composer.placeFooters();

  return {
    pageWidth: ReportTheme.pageWidth,
    pageHeight: ReportTheme.pageHeight,
    pages: composer.pages.map((ops) => ({ ops })),
  };
}

export function wrapText(
  text: string,
  maxWidth: number,
  size: number,
  style: FontStyle,
  measure: TextMeasurer
): string[] {
  const words = text.split(' ').filter((word) => word.length > 0);
  if (words.length === 0) {
    return [''];
  }

  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const candidate = current.length > 0 ? `${current} ${word}` : word;
    if (measure(candidate, size, style) <= maxWidth) {
      current = candidate;
      return;
    }

    if (current.length > 0) {
      lines.push(current);
    }
    if (measure(word, size, style) <= maxWidth) {
      current = word;
      return;
    }

    // A single word wider than the column is broken by character rather than overflowing.
    current = '';
    Array.from(word).forEach((char) => {
      const extended = `${current}${char}`;
      if (current.length === 0 || measure(extended, size, style) <= maxWidth) {
        current = extended;
      } else {
        lines.push(current);
        current = char;
      }
    });
  });

  lines.push(current);
  return lines;
}

class PageComposer {
  readonly pages: LayoutOp[][] = [[]];
  // Full-width cursor for the header and legend; the body flows in `columns` below it.
  private y = CONTENT_TOP;
  private columns: Column[] = [];
  private columnsTop = CONTENT_TOP;
  private hasPlacedTreadLegend = false;
  // Where the previous section ended, when it printed tire cards; null after any other section.
  private lastTireColumn: Column | null = null;

  constructor(private readonly measure: TextMeasurer, private readonly treadLegend: TreadLegendViewModel | null) {}

  placeHeader(header: ReportHeaderViewModel): void {
    const titleLines = wrapText(header.title, CONTENT_WIDTH, ReportTheme.titleSize, FontStyle.Bold, this.measure);
    titleLines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x: CONTENT_LEFT,
        baseline: this.y + index * ReportTheme.titleLineHeight + ReportTheme.titleSize,
        text: line,
        size: ReportTheme.titleSize,
        style: FontStyle.Bold,
        colour: TextColour.Text,
      });
    });
    this.y += titleLines.length * ReportTheme.titleLineHeight;

    const leftLines = [header.customerName, header.date, `Service Advisor: ${header.advisorName}`].filter(
      (line) => line.length > 0
    );
    const rightLines = [
      header.vehicle,
      `Repair Order: ${header.workOrderId}`,
      `Tag: ${header.tag}`,
      `VIN: ${header.vin}`,
    ].filter((line) => line.length > 0);

    const leftLineCount = this.placeTextColumn(leftLines, CONTENT_LEFT);
    const rightLineCount = this.placeTextColumn(rightLines, CONTENT_LEFT + HEADER_COLUMN_WIDTH + HEADER_COLUMN_GAP);
    this.y += Math.max(leftLineCount, rightLineCount) * ReportTheme.headerLineHeight + ReportTheme.headerGap;
  }

  placeLegend(): void {
    const entryHeight = Math.max(ReportTheme.iconSize, ReportTheme.legendSize);
    const height = entryHeight + ReportTheme.legendPadding * 2;
    const top = this.y;

    this.push({
      kind: 'rect',
      x: CONTENT_LEFT,
      top,
      width: CONTENT_WIDTH,
      height,
      thickness: ReportTheme.ruleThickness,
      stroke: StrokeColour.Rule,
    });

    let x = CONTENT_LEFT + ReportTheme.legendPadding;
    LEGEND_ENTRIES.forEach((entry) => {
      this.push({
        kind: 'icon',
        status: entry.status,
        x,
        top: top + ReportTheme.legendPadding + (entryHeight - ReportTheme.iconSize) / 2,
        size: ReportTheme.iconSize,
      });
      x += ReportTheme.iconSize + ReportTheme.iconGap;
      this.push({
        kind: 'text',
        x,
        baseline: top + ReportTheme.legendPadding + (entryHeight + ReportTheme.legendSize) / 2 - 1,
        text: entry.label,
        size: ReportTheme.legendSize,
        style: FontStyle.Regular,
        colour: TextColour.Text,
      });
      x += this.measure(entry.label, ReportTheme.legendSize, FontStyle.Regular) + ReportTheme.legendEntryGap;
    });

    this.y = top + height + ReportTheme.legendGap;
  }

  beginColumns(): void {
    this.columnsTop = this.y;
    this.columns = this.newColumns(this.y);
  }

  // Each section starts in the shorter column (or under the tire section before it) and may
  // continue into the other column, then onto further pages, breaking only between blocks under a
  // "(continued)" heading.
  placeSection(section: SectionViewModel): void {
    const blocks = this.measureSection(section);
    const headingLines = this.wrapHeading(section.heading);
    const lead = this.headingHeight(headingLines) + this.leadHeight(blocks, 0);

    let column = this.startColumnFor(section, lead, this.headingHeight(headingLines) + this.totalHeight(blocks));
    if (!this.fits(column, this.sectionGapFor(column) + lead)) {
      column = this.shorterColumn();
    }
    if (!this.fits(column, this.sectionGapFor(column) + lead)) {
      column = this.startPage();
    } else {
      column.y += this.sectionGapFor(column);
    }
    this.placeHeading(column, headingLines);

    blocks.forEach((block, index) => {
      const needed = this.leadHeight(blocks, index);
      if (!this.fits(column, needed)) {
        column = this.continueSection(column, section.heading, needed);
      }
      block.place(column);
      column.y += block.height;
    });

    this.lastTireColumn = section.tireCards.length > 0 ? column : null;
  }

  // Tire sections arrive together (the view model groups them), so one that follows another
  // starts beneath it rather than in the shorter column, as long as that column is still on the
  // current page. Staying beneath is not worth splitting the section, though: when it would fit
  // whole in the shorter column but not in the previous one, the shorter column wins. Only when
  // neither column holds the whole section does it start beneath and continue as usual.
  private startColumnFor(section: SectionViewModel, lead: number, total: number): Column {
    const previous = this.lastTireColumn;
    const shorter = this.shorterColumn();
    const follows = section.tireCards.length > 0 && previous !== null && this.columns.includes(previous);
    if (!follows) {
      return shorter;
    }
    if (this.fits(previous, this.sectionGapFor(previous) + total)) {
      return previous;
    }
    if (this.fits(shorter, this.sectionGapFor(shorter) + total)) {
      return shorter;
    }
    return this.fits(previous, this.sectionGapFor(previous) + lead) ? previous : shorter;
  }

  private totalHeight(blocks: readonly Block[]): number {
    return blocks.reduce((sum, block) => sum + block.height, 0);
  }

  // Quote Details always starts a fresh page and flows full-width: one block per case, then the
  // quote total. A block that does not fit moves whole to the next page under a "(continued)"
  // title, so a case is never split unless it is taller than a page by itself (each note may run
  // to 5,000 characters); that case flows a line at a time, see flowQuoteCase.
  placeQuoteDetails(quote: QuoteDetailsViewModel): void {
    const continuedTitle = `${QUOTE_DETAILS_TITLE}${CONTINUED_SUFFIX}`;
    const pageCapacity = CONTENT_BOTTOM - CONTENT_TOP - this.quoteTitleHeight(continuedTitle);
    const blocks = [
      ...quote.cases.reduce<Block[]>(
        (measured, quoteCase) => [...measured, ...this.measureQuoteCase(quoteCase, pageCapacity)],
        []
      ),
      this.measureQuoteTotal(quote),
    ];

    let flow = this.startFullWidthPage();
    this.placeQuoteTitle(flow, QUOTE_DETAILS_TITLE);

    blocks.forEach((block, index) => {
      if (!this.fits(flow, this.leadHeight(blocks, index))) {
        flow = this.startFullWidthPage();
        this.placeQuoteTitle(flow, continuedTitle);
      }
      block.place(flow);
      flow.y += block.height;
    });
  }

  placeFooters(): void {
    const total = this.pages.length;
    this.pages.forEach((ops, index) => {
      const text = `Page ${index + 1} of ${total}`;
      const width = this.measure(text, ReportTheme.footerSize, FontStyle.Regular);
      ops.push({
        kind: 'text',
        x: CONTENT_LEFT + (CONTENT_WIDTH - width) / 2,
        baseline: ReportTheme.pageHeight - ReportTheme.margin + ReportTheme.footerSize,
        text,
        size: ReportTheme.footerSize,
        style: FontStyle.Regular,
        colour: TextColour.Muted,
      });
    });
  }

  private measureSection(section: SectionViewModel): Block[] {
    const blocks: Block[] = [];

    if (section.tireCards.length > 0) {
      if (this.treadLegend !== null && !this.hasPlacedTreadLegend) {
        blocks.push(this.measureTreadLegend(this.treadLegend));
        this.hasPlacedTreadLegend = true;
      }
      blocks.push(...this.measureCardGrid(section.tireCards));
    }
    section.rows.forEach((row) => blocks.push(this.measureRow(row)));

    return blocks;
  }

  // The height a block needs on the column it lands in, including any block it is kept with.
  private leadHeight(blocks: readonly Block[], index: number): number {
    const block = blocks[index];
    if (block === undefined) {
      return 0;
    }
    const next = block.keepWithNext ? blocks[index + 1] : undefined;
    return block.height + (next === undefined ? 0 : next.height);
  }

  // A section only ever continues rightwards: one that started in the right column goes to a new
  // page even when the left column has room, because a "(continued)" block sitting to the left of,
  // and below, its own start would be read before it.
  private continueSection(column: Column, heading: string, needed: number): Column {
    const continuedLines = this.wrapHeading(`${heading}${CONTINUED_SUFFIX}`);
    const next = this.columns.indexOf(column) === 0 ? this.columns[1] : null;

    let target: Column;
    if (next !== null && this.fits(next, this.sectionGapFor(next) + this.headingHeight(continuedLines) + needed)) {
      next.y += this.sectionGapFor(next);
      target = next;
    } else {
      target = this.startPage();
    }

    this.placeHeading(target, continuedLines);
    return target;
  }

  private sectionGapFor(column: Column): number {
    return column.y > this.columnsTop ? ReportTheme.sectionGap : 0;
  }

  private shorterColumn(): Column {
    return this.columns.reduce((shortest, column) => (column.y < shortest.y ? column : shortest));
  }

  private measureRow(row: TaskRowViewModel): Block {
    const trailing = this.measureTrailing(row);
    const trailingWidth = trailing.reduce((width, item) => width + item.width + TRAILING_TEXT_GAP, 0);
    const labelWidth = COLUMN_WIDTH - ROW_TEXT_INDENT - trailingWidth;
    const lines = wrapText(row.label, labelWidth, ReportTheme.rowSize, FontStyle.Regular, this.measure);

    return {
      height: Math.max(lines.length * ReportTheme.rowLineHeight, ReportTheme.iconSize + ReportTheme.iconGap),
      keepWithNext: false,
      place: (column) => this.placeRow(column, row, lines, trailing),
    };
  }

  private measureTrailing(row: TaskRowViewModel): TrailingText[] {
    const items: Omit<TrailingText, 'width'>[] = row.measurements.map((text) => ({ text, colour: TextColour.Text }));
    if (row.status === TaskStatus.Grey) {
      items.push({ text: NOT_INSPECTED_LABEL, colour: TextColour.Muted });
    }
    return items.map((item) => ({
      ...item,
      width: this.measure(item.text, ReportTheme.rowSize, FontStyle.Regular),
    }));
  }

  private measureTreadLegend(legend: TreadLegendViewModel): Block {
    const items: LegendItem[] = legend.entries.map((entry) => ({
      status: entry.status,
      text: entry.label,
      width:
        ReportTheme.iconSize +
        ReportTheme.iconGap +
        this.measure(entry.label, ReportTheme.legendSize, FontStyle.Regular),
    }));
    const lines = this.flowLegendItems(items, COLUMN_WIDTH - ReportTheme.legendPadding * 2);
    const height = lines.length * ReportTheme.treadLegendLineHeight + ReportTheme.legendPadding * 2;

    return {
      height: height + ReportTheme.treadLegendGap,
      keepWithNext: true,
      place: (column) => this.placeTreadLegend(column, lines, height),
    };
  }

  // Entries wrap onto further lines when the column is too narrow for all of them in one.
  private flowLegendItems(items: readonly LegendItem[], maxWidth: number): LegendItem[][] {
    const lines: LegendItem[][] = [[]];
    let lineWidth = 0;

    items.forEach((item) => {
      const current = lines[lines.length - 1];
      const extended = current.length === 0 ? item.width : lineWidth + ReportTheme.legendEntryGap + item.width;
      if (current.length > 0 && extended > maxWidth) {
        lines.push([item]);
        lineWidth = item.width;
        return;
      }
      current.push(item);
      lineWidth = extended;
    });

    return lines;
  }

  // Cards print in blocks of four, the first two down the left and the next two down the right,
  // so the four positions keep the driver-side / passenger-side layout of the one-page print and
  // a fifth card (a spare) starts its own row underneath rather than displacing them.
  private measureCardGrid(cards: readonly TireCardViewModel[]): Block[] {
    const measured = cards.map((card) => this.measureCard(card));
    const blocks: Block[] = [];

    for (let start = 0; start < measured.length; start += CARDS_PER_BLOCK) {
      const block = measured.slice(start, start + CARDS_PER_BLOCK);
      const rowCount = Math.ceil(block.length / 2);
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const pair = [block[rowIndex], block[rowIndex + rowCount]].filter(
          (card): card is MeasuredCard => card !== undefined
        );
        const height = Math.max(...pair.map((card) => card.height));
        blocks.push({
          height: height + ReportTheme.cardGap,
          keepWithNext: false,
          place: (column: Column) => this.placeCardRow(column, pair, height),
        });
      }
    }

    return blocks;
  }

  private measureCard(card: TireCardViewModel): MeasuredCard {
    const titleLines = wrapText(
      card.label,
      CARD_WIDTH - CARD_TITLE_TEXT_LEFT - ReportTheme.cardPadding,
      ReportTheme.cardTitleSize,
      FontStyle.Bold,
      this.measure
    );
    const bodyLines = [
      `${PSI_BEFORE_LABEL} ${card.psiBefore}`,
      `${PSI_SET_TO_LABEL} ${card.psiSetTo}`,
      `${TREAD_DEPTH_LABEL} ${card.treadDepth}`,
      ...wrapText(
        `${WEAR_PATTERN_LABEL} ${card.wearPattern.length > 0 ? card.wearPattern : NOT_RECORDED}`,
        CARD_INNER_WIDTH,
        ReportTheme.cardSize,
        FontStyle.Regular,
        this.measure
      ),
      ...card.measurements,
    ];

    return {
      card,
      titleLines,
      bodyLines,
      height:
        this.cardTitleHeight(titleLines) + bodyLines.length * ReportTheme.cardLineHeight + ReportTheme.cardPadding * 2,
    };
  }

  private cardTitleHeight(titleLines: readonly string[]): number {
    return Math.max(titleLines.length * ReportTheme.cardLineHeight, ReportTheme.iconSize) + ReportTheme.cardPadding * 2;
  }

  private wrapHeading(heading: string): string[] {
    return wrapText(heading, COLUMN_WIDTH, ReportTheme.headingSize, FontStyle.Bold, this.measure);
  }

  private headingHeight(headingLines: readonly string[]): number {
    return headingLines.length * ReportTheme.headingLineHeight + HEADING_RULE_HEIGHT;
  }

  private placeHeading(column: Column, headingLines: readonly string[]): void {
    headingLines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x: column.left,
        baseline: column.y + index * ReportTheme.headingLineHeight + ReportTheme.headingSize,
        text: line,
        size: ReportTheme.headingSize,
        style: FontStyle.Bold,
        colour: TextColour.Text,
      });
    });
    const ruleY = column.y + headingLines.length * ReportTheme.headingLineHeight + ReportTheme.headingRuleGap;
    this.push({
      kind: 'line',
      x1: column.left,
      y1: ruleY,
      x2: column.right,
      y2: ruleY,
      thickness: ReportTheme.ruleThickness,
    });
    column.y += this.headingHeight(headingLines);
  }

  private placeRow(
    column: Column,
    row: TaskRowViewModel,
    lines: readonly string[],
    trailing: readonly TrailingText[]
  ): void {
    const top = column.y;

    this.push({
      kind: 'icon',
      status: row.status,
      x: column.left + ReportTheme.rowIndent,
      top: top + (ReportTheme.rowLineHeight - ReportTheme.iconSize) / 2,
      size: ReportTheme.iconSize,
    });

    lines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x: column.left + ROW_TEXT_INDENT,
        baseline: top + index * ReportTheme.rowLineHeight + this.rowBaselineOffset(),
        text: line,
        size: ReportTheme.rowSize,
        style: FontStyle.Regular,
        colour: TextColour.Text,
      });
    });

    let right = column.right;
    trailing
      .slice()
      .reverse()
      .forEach((item) => {
        this.push({
          kind: 'text',
          x: right - item.width,
          baseline: top + this.rowBaselineOffset(),
          text: item.text,
          size: ReportTheme.rowSize,
          style: FontStyle.Regular,
          colour: item.colour,
        });
        right -= item.width + TRAILING_TEXT_GAP;
      });
  }

  private placeTreadLegend(column: Column, lines: readonly (readonly LegendItem[])[], height: number): void {
    const top = column.y;
    this.push({
      kind: 'rect',
      x: column.left,
      top,
      width: COLUMN_WIDTH,
      height,
      thickness: 0,
      stroke: StrokeColour.Rule,
      fill: FillColour.Band,
    });

    lines.forEach((line, lineIndex) => {
      const lineTop = top + ReportTheme.legendPadding + lineIndex * ReportTheme.treadLegendLineHeight;
      const baseline = lineTop + (ReportTheme.treadLegendLineHeight + ReportTheme.legendSize) / 2 - 1;
      let x = column.left + ReportTheme.legendPadding;

      line.forEach((item) => {
        this.push({
          kind: 'icon',
          status: item.status,
          x,
          top: lineTop + (ReportTheme.treadLegendLineHeight - ReportTheme.iconSize) / 2,
          size: ReportTheme.iconSize,
        });
        x += ReportTheme.iconSize + ReportTheme.iconGap;
        this.push({
          kind: 'text',
          x,
          baseline,
          text: item.text,
          size: ReportTheme.legendSize,
          style: FontStyle.Regular,
          colour: TextColour.Text,
        });
        x += this.measure(item.text, ReportTheme.legendSize, FontStyle.Regular) + ReportTheme.legendEntryGap;
      });
    });
  }

  private placeCardRow(column: Column, pair: readonly MeasuredCard[], height: number): void {
    pair.forEach((measured, index) => {
      this.placeCard(column.left + index * (CARD_WIDTH + ReportTheme.cardGap), column.y, measured, height);
    });
  }

  // A bordered card: a filled title band carrying the status icon and task name, then the
  // readings one per line.
  private placeCard(left: number, top: number, measured: MeasuredCard, height: number): void {
    const titleHeight = this.cardTitleHeight(measured.titleLines);

    this.push({
      kind: 'rect',
      x: left,
      top,
      width: CARD_WIDTH,
      height: titleHeight,
      thickness: 0,
      stroke: StrokeColour.Border,
      fill: FillColour.Band,
    });
    this.push({
      kind: 'rect',
      x: left,
      top,
      width: CARD_WIDTH,
      height,
      thickness: ReportTheme.ruleThickness,
      stroke: StrokeColour.Border,
    });
    this.push({
      kind: 'line',
      x1: left,
      y1: top + titleHeight,
      x2: left + CARD_WIDTH,
      y2: top + titleHeight,
      thickness: ReportTheme.ruleThickness,
    });

    this.push({
      kind: 'icon',
      status: measured.card.status,
      x: left + ReportTheme.cardPadding,
      top: top + ReportTheme.cardPadding + (ReportTheme.cardLineHeight - ReportTheme.iconSize) / 2,
      size: ReportTheme.iconSize,
    });
    measured.titleLines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x: left + CARD_TITLE_TEXT_LEFT,
        baseline:
          top +
          ReportTheme.cardPadding +
          index * ReportTheme.cardLineHeight +
          this.cardBaselineOffset(ReportTheme.cardTitleSize),
        text: line,
        size: ReportTheme.cardTitleSize,
        style: FontStyle.Bold,
        colour: TextColour.Text,
      });
    });

    const bodyTop = top + titleHeight + ReportTheme.cardPadding;
    measured.bodyLines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x: left + ReportTheme.cardPadding,
        baseline: bodyTop + index * ReportTheme.cardLineHeight + this.cardBaselineOffset(ReportTheme.cardSize),
        text: line,
        size: ReportTheme.cardSize,
        style: FontStyle.Regular,
        colour: TextColour.Text,
      });
    });
  }

  private quoteTitleHeight(title: string): number {
    const lines = wrapText(title, CONTENT_WIDTH, ReportTheme.titleSize, FontStyle.Bold, this.measure);
    return lines.length * ReportTheme.titleLineHeight + HEADING_RULE_HEIGHT + ReportTheme.quoteTitleGap;
  }

  private placeQuoteTitle(flow: Column, title: string): void {
    const lines = wrapText(title, CONTENT_WIDTH, ReportTheme.titleSize, FontStyle.Bold, this.measure);
    lines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x: flow.left,
        baseline: flow.y + index * ReportTheme.titleLineHeight + ReportTheme.titleSize,
        text: line,
        size: ReportTheme.titleSize,
        style: FontStyle.Bold,
        colour: TextColour.Text,
      });
    });
    const ruleY = flow.y + lines.length * ReportTheme.titleLineHeight + ReportTheme.headingRuleGap;
    this.push({
      kind: 'line',
      x1: flow.left,
      y1: ruleY,
      x2: flow.right,
      y2: ruleY,
      thickness: ReportTheme.ruleThickness,
    });
    flow.y = ruleY + ReportTheme.ruleThickness + ReportTheme.quoteTitleGap;
  }

  // A case: its underlined heading, the hours / expiry and employee lines, then the concern,
  // cause and correction down the left beside the subtotals on the right, and a divider. One
  // block when it fits a page; otherwise it flows (flowQuoteCase).
  private measureQuoteCase(quoteCase: QuoteCaseViewModel, pageCapacity: number): Block[] {
    const heading =
      quoteCase.taskName.length > 0
        ? `${QUOTE_CASE_LABEL} ${quoteCase.caseNumber}${QUOTE_CASE_NAME_SEPARATOR}${quoteCase.taskName}`
        : `${QUOTE_CASE_LABEL} ${quoteCase.caseNumber}`;
    const headingLines = wrapText(heading, CONTENT_WIDTH, ReportTheme.quoteHeadingSize, FontStyle.Bold, this.measure);

    const detailLines = [`${quoteCase.labourHours} ${LABOUR_HOURS_LABEL} - ${EXPIRES_LABEL} ${quoteCase.expires}`];
    if (quoteCase.employee.length > 0) {
      detailLines.push(`${EMPLOYEE_LABEL} ${quoteCase.employee}`);
    }
    const wrappedDetails = detailLines.reduce<string[]>(
      (wrapped, line) => [
        ...wrapped,
        ...wrapText(
          line,
          CONTENT_WIDTH - ReportTheme.quoteFieldIndent,
          ReportTheme.quoteSize,
          FontStyle.Regular,
          this.measure
        ),
      ],
      []
    );

    const fields: QuoteField[] = [
      { label: CONCERN_LABEL, lines: this.wrapQuoteField(quoteCase.concern) },
      { label: CAUSE_LABEL, lines: this.wrapQuoteField(quoteCase.cause) },
      { label: CORRECTION_LABEL, lines: this.wrapQuoteField(quoteCase.correction) },
    ];
    const fieldLines = fields.reduce<QuoteFieldLine[]>(
      (flattened, field) => [
        ...flattened,
        { text: field.label, isLabel: true },
        ...field.lines.map((line) => ({ text: line, isLabel: false })),
      ],
      []
    );
    const amounts: QuoteAmount[] = [
      { label: LABOUR_SUBTOTAL_LABEL, value: quoteCase.labour, style: FontStyle.Regular },
      { label: PARTS_SUBTOTAL_LABEL, value: quoteCase.parts, style: FontStyle.Regular },
      { label: ADDITIONAL_CHARGES_LABEL, value: quoteCase.additionalCharges, style: FontStyle.Regular },
      { label: TAX_LABEL, value: quoteCase.tax, style: FontStyle.Regular },
      { label: CASE_TOTAL_LABEL, value: quoteCase.total, style: FontStyle.Bold },
    ];

    const topHeight =
      headingLines.length * ReportTheme.quoteHeadingLineHeight +
      wrappedDetails.length * ReportTheme.quoteLineHeight +
      ReportTheme.quoteDetailGap;
    const amountsHeight = (1 + amounts.length) * ReportTheme.quoteLineHeight;
    const height =
      topHeight + Math.max(fieldLines.length * ReportTheme.quoteLineHeight, amountsHeight) + QUOTE_CASE_TRAILER_HEIGHT;

    if (height > pageCapacity) {
      return this.flowQuoteCase(headingLines, wrappedDetails, fieldLines, amounts, topHeight, amountsHeight);
    }
    return [
      {
        height,
        keepWithNext: false,
        place: (flow) => {
          const y = this.placeQuoteCaseTop(flow, headingLines, wrappedDetails);
          this.placeQuoteFieldLines(flow, y, fieldLines);
          this.placeQuoteAmounts(flow, y, amounts);
          this.placeQuoteDivider(flow, flow.y + height);
        },
      },
    ];
  }

  // A case taller than a page. The heading, details and subtotals open it with the field lines
  // that sit beside the subtotals; every further line is its own block so the case breaks at a
  // line, a label is kept with the line after it, and the divider travels with the last line.
  private flowQuoteCase(
    headingLines: readonly string[],
    detailLines: readonly string[],
    fieldLines: readonly QuoteFieldLine[],
    amounts: readonly QuoteAmount[],
    topHeight: number,
    amountsHeight: number
  ): Block[] {
    const leadCount = Math.ceil(amountsHeight / ReportTheme.quoteLineHeight);
    const lead = fieldLines.slice(0, leadCount);
    const rest = fieldLines.slice(leadCount);
    const lastLead = lead[lead.length - 1];

    const opening: Block = {
      height:
        topHeight +
        Math.max(lead.length * ReportTheme.quoteLineHeight, amountsHeight) +
        (rest.length === 0 ? QUOTE_CASE_TRAILER_HEIGHT : 0),
      keepWithNext: rest.length > 0 && lastLead !== undefined && lastLead.isLabel,
      place: (flow) => {
        const y = this.placeQuoteCaseTop(flow, headingLines, detailLines);
        this.placeQuoteFieldLines(flow, y, lead);
        this.placeQuoteAmounts(flow, y, amounts);
        if (rest.length === 0) {
          this.placeQuoteDivider(flow, flow.y + opening.height);
        }
      },
    };

    const lineBlocks = rest.map((line, index): Block => {
      const isLast = index === rest.length - 1;
      const height = ReportTheme.quoteLineHeight + (isLast ? QUOTE_CASE_TRAILER_HEIGHT : 0);
      return {
        height,
        keepWithNext: !isLast && line.isLabel,
        place: (flow) => {
          this.placeQuoteFieldLines(flow, flow.y, [line]);
          if (isLast) {
            this.placeQuoteDivider(flow, flow.y + height);
          }
        },
      };
    });

    return [opening, ...lineBlocks];
  }

  private wrapQuoteField(text: string): string[] {
    return wrapText(text, QUOTE_FIELD_WIDTH, ReportTheme.quoteSize, FontStyle.Regular, this.measure);
  }

  // Heading and detail lines; returns the top of the fields / subtotals row beneath them.
  private placeQuoteCaseTop(flow: Column, headingLines: readonly string[], detailLines: readonly string[]): number {
    let y = flow.y;

    headingLines.forEach((line) => {
      this.push({
        kind: 'text',
        x: flow.left,
        baseline:
          y + ReportTheme.quoteHeadingSize + (ReportTheme.quoteHeadingLineHeight - ReportTheme.quoteHeadingSize) / 2,
        text: line,
        size: ReportTheme.quoteHeadingSize,
        style: FontStyle.Bold,
        colour: TextColour.Text,
        underline: true,
      });
      y += ReportTheme.quoteHeadingLineHeight;
    });

    detailLines.forEach((line) => {
      this.pushQuoteText(flow.left + ReportTheme.quoteFieldIndent, y, line, FontStyle.Regular);
      y += ReportTheme.quoteLineHeight;
    });
    return y + ReportTheme.quoteDetailGap;
  }

  private placeQuoteFieldLines(flow: Column, top: number, lines: readonly QuoteFieldLine[]): void {
    lines.forEach((line, index) => {
      const y = top + index * ReportTheme.quoteLineHeight;
      if (line.isLabel) {
        this.pushQuoteText(flow.left, y, line.text, FontStyle.Bold);
      } else {
        this.pushQuoteText(flow.left + ReportTheme.quoteFieldIndent, y, line.text, FontStyle.Regular);
      }
    });
  }

  private placeQuoteAmounts(flow: Column, top: number, amounts: readonly QuoteAmount[]): void {
    this.pushQuoteText(QUOTE_TOTALS_COLUMN_LEFT, top, SUBTOTALS_LABEL, FontStyle.Bold);
    amounts.forEach((amount, index) => {
      const y = top + (1 + index) * ReportTheme.quoteLineHeight;
      this.pushQuoteText(QUOTE_TOTALS_COLUMN_LEFT, y, amount.label, amount.style);
      this.pushQuoteTextRightAligned(flow.right, y, amount.value, amount.style, ReportTheme.quoteSize);
    });
  }

  // The rule that closes a case, sitting the case gap above `bottom` (the end of its block).
  private placeQuoteDivider(flow: Column, bottom: number): void {
    const ruleY = bottom - ReportTheme.quoteCaseGap - ReportTheme.ruleThickness;
    this.push({
      kind: 'line',
      x1: flow.left,
      y1: ruleY,
      x2: flow.right,
      y2: ruleY,
      thickness: ReportTheme.ruleThickness,
    });
  }

  // The quote total and the shop-supplies note, right-aligned beneath the last case.
  private measureQuoteTotal(quote: QuoteDetailsViewModel): Block {
    return {
      height: ReportTheme.quoteHeadingLineHeight + ReportTheme.quoteLineHeight,
      keepWithNext: false,
      place: (flow) => {
        this.pushQuoteTextRightAligned(
          flow.right,
          flow.y + (ReportTheme.quoteHeadingLineHeight - ReportTheme.quoteLineHeight) / 2,
          `${QUOTE_TOTAL_LABEL} ${quote.total}`,
          FontStyle.Bold,
          ReportTheme.quoteHeadingSize
        );
        this.pushQuoteTextRightAligned(
          flow.right,
          flow.y + ReportTheme.quoteHeadingLineHeight,
          SHOP_SUPPLIES_NOTE,
          FontStyle.Regular,
          ReportTheme.quoteNoteSize
        );
      },
    };
  }

  private pushQuoteText(x: number, top: number, text: string, style: FontStyle): void {
    this.push({
      kind: 'text',
      x,
      baseline: top + this.quoteBaselineOffset(ReportTheme.quoteSize),
      text,
      size: ReportTheme.quoteSize,
      style,
      colour: TextColour.Text,
    });
  }

  private pushQuoteTextRightAligned(right: number, top: number, text: string, style: FontStyle, size: number): void {
    this.push({
      kind: 'text',
      x: right - this.measure(text, size, style),
      baseline: top + this.quoteBaselineOffset(size),
      text,
      size,
      style,
      colour: TextColour.Text,
    });
  }

  private quoteBaselineOffset(size: number): number {
    return size + (ReportTheme.quoteLineHeight - size) / 2;
  }

  // Each field wraps within the column so a long name never runs into the other column; returns
  // the number of lines placed so the caller can advance below the taller column.
  private placeTextColumn(fields: readonly string[], x: number): number {
    const lines = fields.reduce<string[]>(
      (wrapped, field) => [
        ...wrapped,
        ...wrapText(field, HEADER_COLUMN_WIDTH, ReportTheme.headerSize, FontStyle.Regular, this.measure),
      ],
      []
    );
    lines.forEach((line, index) => {
      this.push({
        kind: 'text',
        x,
        baseline: this.y + index * ReportTheme.headerLineHeight + ReportTheme.headerSize,
        text: line,
        size: ReportTheme.headerSize,
        style: FontStyle.Regular,
        colour: TextColour.Text,
      });
    });
    return lines.length;
  }

  private rowBaselineOffset(): number {
    return ReportTheme.rowSize + (ReportTheme.rowLineHeight - ReportTheme.rowSize) / 2;
  }

  private cardBaselineOffset(size: number): number {
    return size + (ReportTheme.cardLineHeight - size) / 2;
  }

  private fits(column: Column, height: number): boolean {
    return column.y + height <= CONTENT_BOTTOM;
  }

  private startPage(): Column {
    this.pages.push([]);
    this.columnsTop = CONTENT_TOP;
    this.columns = this.newColumns(CONTENT_TOP);
    return this.columns[0];
  }

  private startFullWidthPage(): Column {
    this.pages.push([]);
    return { left: CONTENT_LEFT, right: CONTENT_RIGHT, y: CONTENT_TOP };
  }

  private newColumns(top: number): Column[] {
    return [
      { left: CONTENT_LEFT, right: CONTENT_LEFT + COLUMN_WIDTH, y: top },
      { left: CONTENT_RIGHT - COLUMN_WIDTH, right: CONTENT_RIGHT, y: top },
    ];
  }

  private push(op: LayoutOp): void {
    this.pages[this.pages.length - 1].push(op);
  }
}
